<template>
  <Authenticated>
    <TrainingNav />
    <div class="form-container">
      <div class="form-header">
        <input v-model="formTitle" placeholder="Form title" class="form-title-input" />
        <textarea v-model="formDescription" placeholder="Form description" class="form-description-input"></textarea>
      </div>
      <div class="roles">
        <h3>Role</h3>
        <p class="roles-hint">Select the role this form applies to</p>
        <RolesComponent :userRoles="roles" @checked="handleCheckedbox" id="roles" :single="true" />
      </div>
      <div class="questions-list">
        <div v-for="(question, index) in questions" :key="index" ref="questionCards" class="question-card">
          <input v-model="question.question" placeholder="Question" class="question-title" />

          <select v-model="question.type" class="question-type" @change="handleQuestionTypeChange(question)">
            <option value="text">Short Answer</option>
            <option value="textarea">Long Answer</option>
            <option value="rating">Rating</option>
            <option value="radio">Select one</option>
            <option value="checkbox">Select multiple</option>
            <option value="checklist">Check List</option>
            <option value="grid">Grid</option>
          </select>

          <div v-if="question.type === 'rating'" class="rating-container">
            <label :for="`rating-max-${question.id}`">Out of</label>
            <input :id="`rating-max-${question.id}`" type="number" v-model.number="question.max" min="2" max="10" class="rating-max-input" />
          </div>

          <div v-if="['radio', 'checkbox', 'checklist'].includes(question.type)" class="options-container">
            <div v-for="(option, optIndex) in question.options" :key="optIndex" class="option-input">
              <input v-model="question.options[optIndex]" placeholder="Option text" :ref="`option-${index}-${optIndex}`" />
              <button @click="removeOption(index, optIndex)" class="btn-remove">×</button>
            </div>
            <button @click="addOption(index)" class="btn-add-option">+ Add option</button>
          </div>

          <div v-if="question.type === 'grid'" class="grid-config">
            <div class="grid-type-toggle">
              <label>Grid type:</label>
              <select v-model="question.gridType" class="grid-type-select">
                <option value="radio">Radio (select one per row)</option>
                <option value="checkbox">Checkbox (select multiple per row)</option>
              </select>
            </div>
            <div class="grid-columns">
              <p class="grid-section-label">Columns</p>
              <div v-for="(col, colIndex) in question.columns" :key="colIndex" class="option-input">
                <input v-model="question.columns[colIndex]" placeholder="Column title" :ref="`col-${index}-${colIndex}`" />
                <button @click="removeGridColumn(index, colIndex)" class="btn-remove">×</button>
              </div>
              <button @click="addGridColumn(index)" class="btn-add-option">+ Add column</button>
            </div>
            <div class="grid-rows">
              <p class="grid-section-label">Rows</p>
              <div v-for="(row, rowIndex) in question.rows" :key="rowIndex" class="option-input">
                <input v-model="question.rows[rowIndex]" placeholder="Row title" :ref="`row-${index}-${rowIndex}`" />
                <button @click="removeGridRow(index, rowIndex)" class="btn-remove">×</button>
              </div>
              <button @click="addGridRow(index)" class="btn-add-option">+ Add row</button>
            </div>
          </div>

          <div class="question-actions">
            <button @click="duplicateQuestion(index)" class="btn-sm btn-duplicate"><font-awesome-icon icon="fa-copy" /> Duplicate</button>
            <button @click="removeQuestion(index)" class="btn-sm btn-danger"><font-awesome-icon icon="fa-trash" /> Delete</button>
          </div>
        </div>
      </div>

      <div class="actions">
        <div class="in-actions">
          <button @click="addQuestion" class="btn-add-question"><font-awesome-icon icon="fa-plus" /> Question</button>
          <button @click="previewForm" class="btn-preview"><font-awesome-icon icon="fa-eye" /> Preview</button>
        </div>
        <div class="more-actions">
          <button @click="cancelForm" class="btn-cancel"><font-awesome-icon icon="fa-xmark" /> Cancel</button>
          <button @click="clearForm" class="btn-clear"><font-awesome-icon icon="fa-trash" /> Clear form</button>
          <button @click="save" class="btn-save"><font-awesome-icon icon="fa-floppy-disk" /> Save</button>
        </div>
      </div>
    </div>
    <Confirm :open="showConfirm" :message="confirmMessage" :isDeleteConfirm="true" @confirmModal="handleConfirm" @close="showConfirm = false" />
    <PreviewFormModal :isOpen="openModal" @close="openModal = false" :form="previewData" />
  </Authenticated>
</template>

<script>
import Authenticated from "../_layouts/Authenticated.vue";
import PreviewFormModal from "./PreviewFormModal.vue";
import TrainingNav from "./TrainingNav.vue";
import RolesComponent from "./RolesComponent.vue";
import { Confirm } from "@monolith/components";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "TrainingForm",
  components: {
    Authenticated,
    PreviewFormModal,
    TrainingNav,
    Confirm,
    RolesComponent,
  },
  data() {
    return {
      formTitle: "",
      formDescription: "",
      questions: [
        {
          id: Date.now(),
          question: "",
          type: "text",
          options: [],
        },
      ],
      showConfirm: false,
      confirmMessage: "",
      openModal: false,
      roles: [],
    };
  },
  async mounted() {
    if (await this.isEditing) {
      const editForm = this.getEditForm;
      this.formTitle = editForm.title;
      this.formDescription = editForm.description;
      this.questions = (editForm.questions || []).map((question) => ({
        ...question,
        options: [...(question.options || [])],
      }));
      this.roles = [...(editForm.roles || [])];
    }
  },
  computed: {
    ...mapGetters("training", ["isEditing", "getEditForm", "getRoles"]),
    previewData() {
      return {
        title: this.formTitle,
        desc: this.formDescription,
        questions: this.questions,
      };
    },
  },

  watch: {},
  methods: {
    ...mapActions("training", ["saveForm", "editing"]),

    clearForm() {
      this.confirmMessage = "Are you sure you want to clear the form? This action cannot be undone.";
      this.showConfirm = true;
    },

    handleConfirm() {
      this.showConfirm = false;
      this.formTitle = "";
      this.formDescription = "";
      this.roles = [];
      this.questions = [
        {
          id: Date.now(),
          question: "",
          type: "text",
          options: [],
        },
      ];
    },

    handleCheckedbox(checkedBoxes) {
      this.roles = checkedBoxes;
    },

    async save() {
      const formData = {
        title: this.formTitle,
        description: this.formDescription,
        roles: this.roles,
        questions: this.questions,
        docType: "form",
      };
      if ("_id" in this.getEditForm) {
        formData._id = this.getEditForm._id;
        formData._rev = this.getEditForm._rev;
      }
      await this.saveForm(formData);
      this.$router.push("/training/templates");
    },

    addQuestion() {
      this.questions.push({
        id: Date.now(),
        question: "",
        type: "text",
        options: [],
      });

      this.$nextTick(() => {
        const cards = this.$refs.questionCards;
        const lastCard = Array.isArray(cards) ? cards[cards.length - 1] : cards;
        if (lastCard && typeof lastCard.scrollIntoView === "function") {
          lastCard.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },

    duplicateQuestion(index) {
      const question = this.questions[index];
      this.questions.splice(index + 1, 0, {
        ...question,
        id: Date.now(),
        options: [...(question.options || [])],
        rows: question.rows ? [...question.rows] : undefined,
        columns: question.columns ? [...question.columns] : undefined,
      });

      this.$nextTick(() => {
        const cards = this.$refs.questionCards;
        const newCard = Array.isArray(cards) ? cards[index + 1] : null;
        if (newCard && typeof newCard.scrollIntoView === "function") {
          newCard.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },

    handleQuestionTypeChange(question) {
      if (question.type === "rating") {
        question.max = question.max || 5;
        question.options = [];
        delete question.rows;
        delete question.columns;
        delete question.gridType;
        return;
      }

      if (question.type === "grid") {
        question.rows = question.rows || ["Row 1", "Row 2"];
        question.columns = question.columns || ["Column 1", "Column 2", "Column 3"];
        question.gridType = question.gridType || "radio";
        question.options = [];
        delete question.max;
        return;
      }

      if ("max" in question) delete question.max;
      if ("rows" in question) delete question.rows;
      if ("columns" in question) delete question.columns;
      if ("gridType" in question) delete question.gridType;
    },

    removeQuestion(index) {
      this.questions.splice(index, 1);
    },

    addOption(index) {
      this.questions[index].options.push("");
      const optIndex = this.questions[index].options.length - 1;
      this.$nextTick(() => {
        const ref = this.$refs[`option-${index}-${optIndex}`];
        const el = Array.isArray(ref) ? ref[ref.length - 1] : ref;
        if (el) el.focus();
      });
    },

    removeOption(index, optIndex) {
      this.questions[index].options.splice(optIndex, 1);
    },

    addGridRow(index) {
      this.questions[index].rows.push("");
      const rowIndex = this.questions[index].rows.length - 1;
      this.$nextTick(() => {
        const ref = this.$refs[`row-${index}-${rowIndex}`];
        const el = Array.isArray(ref) ? ref[ref.length - 1] : ref;
        if (el) el.focus();
      });
    },

    removeGridRow(index, rowIndex) {
      this.questions[index].rows.splice(rowIndex, 1);
    },

    addGridColumn(index) {
      this.questions[index].columns.push("");
      const colIndex = this.questions[index].columns.length - 1;
      this.$nextTick(() => {
        const ref = this.$refs[`col-${index}-${colIndex}`];
        const el = Array.isArray(ref) ? ref[ref.length - 1] : ref;
        if (el) el.focus();
      });
    },

    removeGridColumn(index, colIndex) {
      this.questions[index].columns.splice(colIndex, 1);
    },

    previewForm() {
      this.openModal = true;
      // Add preview logic here
    },

    cancelForm() {
      if (this.isEditing) {
        const editForm = this.getEditForm || {};
        this.formTitle = editForm.title || "";
        this.formDescription = editForm.description || "";
        this.roles = [...(editForm.roles || [])];
        this.questions = (editForm.questions || []).length
          ? editForm.questions.map((question) => ({
              ...question,
              options: [...(question.options || [])],
            }))
          : [
              {
                id: Date.now(),
                question: "",
                type: "text",
                options: [],
              },
            ];
        this.editing(false);
      } else {
        this.formTitle = "";
        this.formDescription = "";
        this.roles = [];
        this.questions = [
          {
            id: Date.now(),
            question: "",
            type: "text",
            options: [],
          },
        ];
      }
      this.$router.push("/training/templates");
    },
  },
};
</script>

<style scoped lang="scss">
.form-container {
  margin: 1rem;
  padding: 1.5rem;
  padding-bottom: 7.25rem;
  background-color: var(--primary-colour);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
}

.roles {
  background: var(--secondary-colour);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
  h3 {
    margin: 0 0 0.25rem;
    color: var(--text-colour);
  }
  .roles-hint {
    margin: 0 0 0.75rem;
    font-size: 0.82rem;
    color: var(--text-colour);
    opacity: 0.55;
  }
}

.form-header {
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-colour);

  input,
  textarea {
    width: calc(100% - 1.5rem);
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border: 1px solid var(--border-colour);
    border-radius: 0.3rem;
    background-color: var(--secondary-colour);
    color: var(--text-colour);
    font-size: 1rem;
    transition: border-color 0.2s ease;
    &:focus {
      outline: none;
      border-color: var(--action-colour);
    }
    &::placeholder {
      color: var(--text-colour);
      opacity: 0.4;
    }
  }

  .form-title-input {
    font-size: 1.2rem;
    font-weight: 600;
  }

  .form-description-input {
    resize: vertical;
    min-height: 70px;
    margin-bottom: 0;
  }
}

.questions-list {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.question-card {
  padding: 1.25rem;
  background-color: var(--secondary-colour);
  border: 1px solid var(--border-colour);
  border-left: 3px solid var(--action-colour);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  .question-title,
  .question-type {
    width: calc(100% - 1.5rem);
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.75rem;
    border: 1px solid var(--border-colour);
    border-radius: 0.3rem;
    background-color: var(--primary-colour);
    color: var(--text-colour);
    font-size: 0.95rem;
    cursor: pointer;
    transition: border-color 0.2s ease;
    &:focus {
      outline: none;
      border-color: var(--action-colour);
    }
    &::placeholder {
      color: var(--text-colour);
      opacity: 0.4;
    }
    option {
      background-color: var(--primary-colour);
      color: var(--text-colour);
    }
  }

  .rating-container {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: var(--primary-colour);
    border-radius: 0.3rem;
    border-left: 3px solid var(--action-colour);

    label {
      font-size: 0.95rem;
      color: var(--text-colour);
      white-space: nowrap;
    }

    .rating-max-input {
      width: 5rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--border-colour);
      border-radius: 0.3rem;
      background-color: var(--secondary-colour);
      color: var(--text-colour);
      font-size: 0.95rem;
      transition: border-color 0.2s ease;
      &:focus {
        outline: none;
        border-color: var(--action-colour);
      }
    }
  }

  .grid-config {
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: var(--primary-colour);
    border-radius: 0.3rem;
    border-left: 3px solid var(--action-colour);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .grid-type-toggle {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      label {
        font-size: 0.9rem;
        color: var(--text-colour);
        white-space: nowrap;
      }
      .grid-type-select {
        padding: 0.4rem 0.6rem;
        border: 1px solid var(--border-colour);
        border-radius: 0.3rem;
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        font-size: 0.9rem;
        cursor: pointer;
        &:focus {
          outline: none;
          border-color: var(--action-colour);
        }
        option {
          background-color: var(--secondary-colour);
          color: var(--text-colour);
        }
      }
    }

    .grid-section-label {
      margin: 0 0 0.4rem;
      font-size: 0.82rem;
      font-weight: 600;
      color: var(--text-colour);
      opacity: 0.7;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .option-input {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      align-items: center;
      input {
        flex: 1;
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
        border: 1px solid var(--border-colour);
        border-radius: 0.3rem;
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        transition: border-color 0.2s ease;
        &:focus {
          outline: none;
          border-color: var(--action-colour);
        }
        &::placeholder {
          color: var(--text-colour);
          opacity: 0.4;
        }
      }
    }

    .btn-remove {
      padding: 0.4rem 0.6rem;
      border: 1px solid var(--warning-colour);
      background-color: var(--warning-colour);
      color: var(--primary-colour);
      border-radius: 0.3rem;
      cursor: pointer;
      font-size: 0.85rem;
      transition: opacity 0.2s ease;
      &:hover { opacity: 0.85; }
    }

    .btn-add-option {
      padding: 0.4rem 0.9rem;
      margin-top: 0.25rem;
      border: 1px dashed var(--action-colour);
      background-color: transparent;
      color: var(--action-colour);
      border-radius: 0.3rem;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease, border-style 0.2s ease;
      &:hover {
        background-color: var(--opacity-background);
        border-style: solid;
      }
    }
  }

  .options-container {
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: var(--primary-colour);
    border-radius: 0.3rem;
    border-left: 3px solid var(--action-colour);

    .option-input {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      align-items: center;

      input {
        flex: 1;
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
        border: 1px solid var(--border-colour);
        border-radius: 0.3rem;
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        transition: border-color 0.2s ease;
        &:focus {
          outline: none;
          border-color: var(--action-colour);
        }
        &::placeholder {
          color: var(--text-colour);
          opacity: 0.4;
        }
      }
    }

    .btn-remove {
      padding: 0.4rem 0.6rem;
      border: 1px solid var(--warning-colour);
      background-color: var(--warning-colour);
      color: var(--primary-colour);
      border-radius: 0.3rem;
      cursor: pointer;
      font-size: 0.85rem;
      transition: opacity 0.2s ease;
      &:hover {
        opacity: 0.85;
      }
    }

    .btn-add-option {
      padding: 0.4rem 0.9rem;
      margin-top: 0.25rem;
      border: 1px dashed var(--action-colour);
      background-color: transparent;
      color: var(--action-colour);
      border-radius: 0.3rem;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease, border-style 0.2s ease;
      &:hover {
        background-color: var(--opacity-background);
        border-style: solid;
      }
    }
  }

  .question-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-colour);

    .grow {
      flex-grow: 1;
    }

    .btn-sm {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.5rem 1rem;
      border: 1px solid var(--secondary-colour);
      background-color: var(--secondary-colour);
      color: var(--text-colour);
      border-radius: 0.3rem;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: opacity 0.2s ease;
      &:hover {
        opacity: 0.8;
      }

      &.btn-danger {
        border-color: var(--warning-colour);
        background-color: var(--warning-colour);
        color: var(--primary-colour);
        &:hover {
          opacity: 0.85;
        }
      }

      &.btn-duplicate {
        border-color: var(--action-colour);
        background-color: var(--action-colour);
        color: var(--primary-colour);
      }
    }
  }
}

.actions {
  display: flex;
  position: fixed;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  width: min(72rem, calc(100vw - 2rem));
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-colour);
  border-radius: 0.5rem;
  background-color: var(--opacity-background);
  box-shadow: var(--box-shadow);
  z-index: 30;

  .in-actions,
  .more-actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    border-radius: 0.3rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;
    &:hover {
      opacity: 0.85;
    }
  }

  .btn-add-question {
    border: 1px solid var(--action-colour);
    background-color: var(--action-colour);
    color: var(--primary-colour);
  }

  .btn-preview {
    border: 1px solid var(--action-colour);
    background-color: transparent;
    color: var(--action-colour);
    &:hover {
      background-color: var(--action-colour);
      color: var(--primary-colour);
      opacity: 1;
    }
  }

  .btn-clear {
    border: 1px solid var(--warning-colour);
    background-color: var(--warning-colour);
    color: var(--primary-colour);
  }

  .btn-cancel {
    border: 1px solid var(--border-colour);
    background-color: var(--secondary-colour);
    color: var(--text-colour);
  }

  .btn-save {
    border: 1px solid var(--success-colour);
    background-color: var(--success-colour);
    color: var(--primary-colour);
  }
}

@media (max-width: 760px) {
  .form-container {
    padding-bottom: 11.5rem;
  }

  .actions {
    width: calc(100vw - 1rem);
    bottom: 0.5rem;
    padding: 0.6rem;
    gap: 0.5rem;
    flex-direction: column;
    align-items: stretch;

    .in-actions,
    .more-actions {
      width: 100%;
      flex-wrap: wrap;
      justify-content: center;
    }

    button {
      flex: 1 1 9rem;
      justify-content: center;
    }
  }
}
</style>
